/**
 * CL-409J — Canonical Planning capability resolver.
 *
 * Single source of truth for Planning permissions in the UI and unit tests.
 * Must stay aligned with firestore.rules helpers:
 *   pm_collaborator_capability, pm_can_edit_estimate, pm_can_publish_estimate,
 *   pm_can_read_draft_estimate (alias), pm_collaborator_can_view_planning_photos.
 */

import {
  COLLABORATOR_PERMISSION_KEYS,
  normalizeCollaboratorPermissions,
  buildDefaultCollaboratorPermissions,
} from "./projectCollaboratorPermissions";
import { CLIENT_PORTAL_MODULE_PERMISSION_KEY_LIST } from "./clientPortalModulePermissionKeys";
import { DESIGN_BOARD_PERMISSION_KEY_LIST } from "./designBoardPermissionKeys";
import { normalizeSchedulePermissionsForAssignmentSave } from "./projectSchedulePermissionsEditorModel";
import {
  buildEmptyClientPortalModulePermissionFlags,
  isValidCanonicalAuthAssignment,
  readExplicitClientModuleGrant,
  resolveAssignmentFirstClientPortalModulePermissions,
} from "./clientPortalAssignmentRuntimeModel";
import { CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY } from "./projectSupplierPermissionsEditorModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function emailLower(value) {
  return safeStr(value).toLowerCase();
}

function permissionsObjectHasField(doc) {
  return doc?.permissions != null && typeof doc.permissions === "object";
}

function readPermissionBoolean(source, key) {
  const value = source?.permissions?.[key];
  return typeof value === "boolean" ? value : undefined;
}

function readAssignmentCapabilityValue(assignmentDoc, key) {
  return readPermissionBoolean(assignmentDoc, key);
}

function readDenormCapabilityValue(project, authUid, authEmailNorm, key) {
  const uid = safeStr(authUid);
  const em = emailLower(authEmailNorm);
  const byUid = uid ? project?.collaboratorPermissionsByUid?.[uid] : null;
  const byEmail = em ? project?.collaboratorPermissionsByEmail?.[em] : null;
  if (typeof byUid?.[key] === "boolean") return byUid[key];
  if (typeof byEmail?.[key] === "boolean") return byEmail[key];
  return undefined;
}

function readMatchedCollaboratorCapabilityValue(matchedCollaborator, key) {
  return readPermissionBoolean(matchedCollaborator, key);
}

function resolveExplicitCapabilityValue({
  project = null,
  matchedCollaborator = null,
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
  authUid = "",
  authEmail = "",
  capabilityKey = "",
} = {}) {
  const key = safeStr(capabilityKey);
  if (!key) return undefined;
  const uidAssignmentValue = readAssignmentCapabilityValue(assignmentUidDoc, key);
  if (uidAssignmentValue !== undefined) return uidAssignmentValue;
  const emailAssignmentValue = readAssignmentCapabilityValue(assignmentEmailDoc, key);
  if (emailAssignmentValue !== undefined) return emailAssignmentValue;
  const denormValue = readDenormCapabilityValue(project, authUid, authEmail, key);
  if (denormValue !== undefined) return denormValue;
  return readMatchedCollaboratorCapabilityValue(matchedCollaborator, key);
}

/**
 * Mirrors Firestore pm_collaborator_capability(pmProjectId, key).
 */
export function resolveCollaboratorCapability({
  isAdmin = false,
  project = null,
  matchedCollaborator = null,
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
  authUid = "",
  authEmail = "",
  capabilityKey = "",
} = {}) {
  const key = safeStr(capabilityKey);
  if (!key) return false;
  if (isAdmin) return true;
  return (
    resolveExplicitCapabilityValue({
      project,
      matchedCollaborator,
      assignmentUidDoc,
      assignmentEmailDoc,
      authUid,
      authEmail,
      capabilityKey: key,
    }) === true
  );
}

/**
 * Mirrors Firestore pm_assignment_has_permissions_field.
 */
export function assignmentHasPermissionsField({
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
} = {}) {
  return permissionsObjectHasField(assignmentUidDoc) || permissionsObjectHasField(assignmentEmailDoc);
}

/**
 * Mirrors Firestore pm_collaborator_full_project_on_assignment (uid + email assignment docs).
 */
export function resolveCollaboratorFullProjectAssignment({
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
} = {}) {
  const check = (doc) => {
    if (!doc) return false;
    const status = safeStr(doc.status || "active").toLowerCase();
    if (status !== "active") return false;
    if (safeStr(doc.role).toLowerCase() === "client") return false;
    return safeStr(doc.accessScope || "full_project").toLowerCase() === "full_project";
  };
  return check(assignmentUidDoc) || check(assignmentEmailDoc);
}

/**
 * Mirrors Firestore pm_assignment_exists_for_user.
 */
export function assignmentExistsForUser({
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
} = {}) {
  return !!assignmentUidDoc || !!assignmentEmailDoc;
}

/**
 * Mirrors Firestore pm_legacy_collaborator_estimate_access && !pm_client_on_project.
 */
export function resolveLegacyCollaboratorPlanningAccess({
  isCollaboratorOnProject = false,
  isClientOnProject = false,
  hasFullProjectAssignment = false,
} = {}) {
  if (isClientOnProject) return false;
  return isCollaboratorOnProject || hasFullProjectAssignment;
}

/**
 * Mirrors Firestore pm_can_edit_estimate / pm_can_read_draft_estimate (identical).
 */
export function resolveCanEditDraftEstimate(context = {}) {
  if (context.isAdmin) return true;
  if (!context.hasFullProjectAssignment) return false;
  const explicitValue = resolveExplicitCapabilityValue({ ...context, capabilityKey: "canEditEstimate" });
  if (explicitValue !== undefined) return explicitValue;
  if (
    !assignmentHasPermissionsField(context) &&
    resolveLegacyCollaboratorPlanningAccess({
      isCollaboratorOnProject: context.isCollaboratorOnProject,
      isClientOnProject: context.isClientOnProject,
      hasFullProjectAssignment: context.hasFullProjectAssignment,
    })
  ) {
    return true;
  }
  return false;
}

/** @deprecated Use resolveCanEditDraftEstimate — read and edit share one gate. */
export const resolveCanReadDraftEstimate = resolveCanEditDraftEstimate;

/**
 * Mirrors Firestore pm_can_publish_estimate.
 */
export function resolveCanPublishEstimate(context = {}) {
  if (context.isAdmin) return true;
  if (!context.hasFullProjectAssignment) return false;
  const explicitValue = resolveExplicitCapabilityValue({ ...context, capabilityKey: "canPublishEstimate" });
  return explicitValue === true;
}

/**
 * Mirrors Firestore pm_can_edit_scope.
 */
export function resolveCanEditScope(context = {}) {
  if (context.isAdmin) return true;
  if (!context.hasFullProjectAssignment) return false;
  const explicitValue = resolveExplicitCapabilityValue({ ...context, capabilityKey: "canEditScope" });
  return explicitValue === true;
}

/**
 * Mirrors Firestore pm_collaborator_can_view_planning_photos.
 * Clients use pm_client_on_project in rules — pass isClientOnProject separately.
 */
export function resolveCanViewPlanningPhotos(context = {}) {
  if (context.isAdmin) return true;
  const explicitValue = resolveExplicitCapabilityValue({ ...context, capabilityKey: "canViewPhotos" });
  if (explicitValue === false) return false;
  if (context.isClientOnProject) return true;
  if (!context.hasFullProjectAssignment) return false;
  return explicitValue === true;
}

function applyPermissionImplications(permissions = {}, context = {}, explicitValues = {}) {
  const next = { ...permissions };
  if (next.canEditScope && !next.canViewPlanning && explicitValues.canViewPlanning !== false) {
    next.canViewPlanning = true;
  }
  if (next.canEditEstimate && !next.canViewPlanning && explicitValues.canViewPlanning !== false) {
    next.canViewPlanning = true;
  }
  if (next.canPublishEstimate) {
    if (explicitValues.canViewPlanning !== false) next.canViewPlanning = true;
    if (explicitValues.canEditEstimate !== false) next.canEditEstimate = true;
  }
  if (next.canLogTime && !next.canViewTimeLog && explicitValues.canViewTimeLog !== false) {
    next.canViewTimeLog = true;
  }
  return normalizeCollaboratorPermissions(next, context);
}

/**
 * Portal file list: non-full-project collaborators stay scoped to explicitly shared files.
 */
export function resolveShouldUseSharedFilesOnlyForPortalFiles({
  matchedCollaborator = null,
  isHistoricalArchiveProject = false,
} = {}) {
  if (isHistoricalArchiveProject) return false;
  const accessScope = safeStr(matchedCollaborator?.accessScope || "").toLowerCase();
  if (!accessScope || accessScope === "full_project") return false;
  return true;
}

/**
 * Build a full Planning capability context from project + viewer + optional assignment docs.
 */
export function buildPlanningCapabilityContext({
  project = null,
  authUid = "",
  authEmail = "",
  isAdmin = false,
  matchedCollaborator = null,
  viewerProjectPersona = "guest",
  assignmentUidDoc = null,
  assignmentEmailDoc = null,
  isClientOnProject = null,
  isCollaboratorOnProject = null,
} = {}) {
  const uid = safeStr(authUid);
  const em = emailLower(authEmail);
  const collabRows = Array.isArray(project?.collaborators) ? project.collaborators : [];

  const row =
    matchedCollaborator ||
    collabRows.find((r) => {
      if (safeStr(r?.status).toLowerCase() === "removed") return false;
      const rowUid = safeStr(r?.uid);
      const rowAuthUid = safeStr(r?.authUid);
      const rowEmail = emailLower(r?.emailLower || r?.email);
      return (uid && (rowUid === uid || rowAuthUid === uid)) || (em && rowEmail && rowEmail === em);
    }) ||
    null;

  const clientOnProject =
    isClientOnProject != null
      ? isClientOnProject
      : viewerProjectPersona === "client" ||
        (uid && safeStr(project?.clientUid) === uid) ||
        (em &&
          (emailLower(project?.clientEmail) === em ||
            emailLower(project?.clientEmailLower) === em));

  const collaboratorOnProject =
    isCollaboratorOnProject != null
      ? isCollaboratorOnProject
      : !!row ||
        (uid && Array.isArray(project?.collaboratorUserIds) && project.collaboratorUserIds.includes(uid)) ||
        (em && Array.isArray(project?.collaboratorEmails) && project.collaboratorEmails.includes(em));

  const hasFullProjectAssignment = resolveCollaboratorFullProjectAssignment({
    assignmentUidDoc,
    assignmentEmailDoc,
  });

  const accessScope = safeStr(row?.accessScope || "full_project").toLowerCase();
  const legacyFullProject =
    !assignmentExistsForUser({ assignmentUidDoc, assignmentEmailDoc }) &&
    collaboratorOnProject &&
    accessScope === "full_project";

  return {
    isAdmin,
    project,
    authUid: uid,
    authEmail: em,
    assignmentUidDoc,
    assignmentEmailDoc,
    matchedCollaborator: row,
    viewerProjectPersona,
    isClientOnProject: clientOnProject,
    isCollaboratorOnProject: collaboratorOnProject,
    hasFullProjectAssignment: hasFullProjectAssignment || legacyFullProject,
  };
}

/**
 * Resolve explicit client-portal module flags from assignment, denorm, or collaborator row.
 * These keys are separate from COLLABORATOR_PERMISSION_KEYS and must not be role-defaulted away.
 */
function buildEmptyDesignBoardPermissionFlags() {
  return DESIGN_BOARD_PERMISSION_KEY_LIST.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});
}

/**
 * CL-DESIGN-BOARDS-DB-2 — explicit design board flags from canonical assignment (fail closed).
 */
export function resolveDesignBoardAssignmentPermissionFlags(context = {}) {
  const ctx =
    context.matchedCollaborator != null || context.project
      ? buildPlanningCapabilityContext(context)
      : context;

  if (context.assignmentFirstClientModules === true) {
    if (context.clientAssignmentLoading === true) {
      return buildEmptyDesignBoardPermissionFlags();
    }
    const flags = buildEmptyDesignBoardPermissionFlags();
    if (
      !isValidCanonicalAuthAssignment(ctx.assignmentUidDoc, {
        projectId: safeStr(ctx.project?.id),
        authUid: ctx.authUid,
      })
    ) {
      return flags;
    }
    const permissions =
      ctx.assignmentUidDoc?.permissions && typeof ctx.assignmentUidDoc.permissions === "object"
        ? ctx.assignmentUidDoc.permissions
        : {};
    DESIGN_BOARD_PERMISSION_KEY_LIST.forEach((key) => {
      flags[key] = readExplicitClientModuleGrant(permissions, key);
    });
    return flags;
  }

  const flags = buildEmptyDesignBoardPermissionFlags();
  DESIGN_BOARD_PERMISSION_KEY_LIST.forEach((key) => {
    const explicitValue = resolveExplicitCapabilityValue({ ...ctx, capabilityKey: key });
    flags[key] = explicitValue === true;
  });
  return flags;
}

export function resolveClientPortalModulePermissions(context = {}) {
  const ctx =
    context.matchedCollaborator != null || context.project
      ? buildPlanningCapabilityContext(context)
      : context;

  if (context.assignmentFirstClientModules === true) {
    if (context.clientAssignmentLoading === true) {
      return buildEmptyClientPortalModulePermissionFlags();
    }
    return resolveAssignmentFirstClientPortalModulePermissions({
      assignmentDoc: ctx.assignmentUidDoc,
      projectId: safeStr(ctx.project?.id),
      authUid: ctx.authUid,
    });
  }

  const flags = {};
  CLIENT_PORTAL_MODULE_PERMISSION_KEY_LIST.forEach((key) => {
    const explicitValue = resolveExplicitCapabilityValue({ ...ctx, capabilityKey: key });
    flags[key] = explicitValue === true;
  });
  return flags;
}

/**
 * CL-PRODUCT-SELECTIONS-006B1 — explicit client supplier interaction contribution flag.
 * Separate from portal module keys; must not be role-defaulted away.
 */
export function resolveClientProductSelectionDecisionPermissions(context = {}) {
  const ctx =
    context.matchedCollaborator != null || context.project
      ? buildPlanningCapabilityContext(context)
      : context;
  const key = "canRespondToProductSelections";

  if (context.assignmentFirstClientModules === true) {
    if (context.clientAssignmentLoading === true) {
      return { [key]: false };
    }
    const flags = resolveAssignmentFirstClientPortalModulePermissions({
      assignmentDoc: ctx.assignmentUidDoc,
      projectId: safeStr(ctx.project?.id),
      authUid: ctx.authUid,
    });
    if (!isValidCanonicalAuthAssignment(ctx.assignmentUidDoc, { projectId: safeStr(ctx.project?.id), authUid: ctx.authUid })) {
      return { [key]: false };
    }
    const permissions =
      ctx.assignmentUidDoc?.permissions && typeof ctx.assignmentUidDoc.permissions === "object"
        ? ctx.assignmentUidDoc.permissions
        : {};
    return {
      [key]:
        readExplicitClientModuleGrant(permissions, key) &&
        flags.canViewSuppliersAndSelections === true,
    };
  }

  const decideExplicit = resolveExplicitCapabilityValue({ ...ctx, capabilityKey: key });
  const viewExplicit = resolveExplicitCapabilityValue({
    ...ctx,
    capabilityKey: "canViewSuppliersAndSelections",
  });
  return {
    [key]: decideExplicit === true && viewExplicit === true,
  };
}

export function resolveClientSupplierInteractionPermissions(context = {}) {
  const ctx =
    context.matchedCollaborator != null || context.project
      ? buildPlanningCapabilityContext(context)
      : context;

  if (context.assignmentFirstClientModules === true) {
    if (context.clientAssignmentLoading === true) {
      return { [CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY]: false };
    }
    const flags = resolveAssignmentFirstClientPortalModulePermissions({
      assignmentDoc: ctx.assignmentUidDoc,
      projectId: safeStr(ctx.project?.id),
      authUid: ctx.authUid,
    });
    if (!isValidCanonicalAuthAssignment(ctx.assignmentUidDoc, { projectId: safeStr(ctx.project?.id), authUid: ctx.authUid })) {
      return { [CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY]: false };
    }
    const permissions =
      ctx.assignmentUidDoc?.permissions && typeof ctx.assignmentUidDoc.permissions === "object"
        ? ctx.assignmentUidDoc.permissions
        : {};
    return {
      [CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY]:
        readExplicitClientModuleGrant(permissions, CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY) &&
        flags.canViewSuppliersAndSelections === true,
    };
  }

  const contributeExplicit = resolveExplicitCapabilityValue({
    ...ctx,
    capabilityKey: CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY,
  });
  const viewExplicit = resolveExplicitCapabilityValue({
    ...ctx,
    capabilityKey: "canViewSuppliersAndSelections",
  });
  return {
    [CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY]:
      contributeExplicit === true && viewExplicit === true,
  };
}

/**
 * Resolve all Planning-related capabilities for a viewer using the canonical model.
 */
export function resolvePlanningCapabilities(context = {}) {
  const ctx = context.matchedCollaborator != null || context.project
    ? buildPlanningCapabilityContext(context)
    : context;

  if (ctx.isAdmin) {
    return COLLABORATOR_PERMISSION_KEYS.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
  }

  const hasCanonicalAssignmentDoc =
    context.assignmentFirstClientModules === true
    && context.assignmentUidDoc
    && typeof context.assignmentUidDoc === "object";
  if (ctx.viewerProjectPersona === "client" && !ctx.matchedCollaborator && !hasCanonicalAssignmentDoc) {
    return buildDefaultCollaboratorPermissions({ role: "client", accessScope: "full_project" });
  }

  const role = safeStr(ctx.matchedCollaborator?.role || "consultant").toLowerCase() || "consultant";
  const accessScope = safeStr(ctx.matchedCollaborator?.accessScope || "full_project").toLowerCase() || "full_project";
  const base = buildDefaultCollaboratorPermissions({ role, accessScope });

  const resolved = { ...base };
  const explicitValues = {};
  COLLABORATOR_PERMISSION_KEYS.forEach((key) => {
    const explicitValue = resolveExplicitCapabilityValue({ ...ctx, capabilityKey: key });
    if (explicitValue !== undefined) {
      explicitValues[key] = explicitValue;
      resolved[key] = explicitValue;
      return;
    }
    if (key === "canEditEstimate") {
      resolved[key] = resolveCanEditDraftEstimate(ctx);
      return;
    }
    if (key === "canPublishEstimate") {
      resolved[key] = resolveCanPublishEstimate(ctx);
      return;
    }
    if (key === "canEditScope") {
      resolved[key] = resolveCanEditScope(ctx);
      return;
    }
    if (key === "canViewPhotos") {
      resolved[key] = resolveCanViewPlanningPhotos(ctx);
      return;
    }
    if (key === "canViewPlanning") {
      resolved[key] =
        ctx.isClientOnProject ||
        (ctx.hasFullProjectAssignment &&
          (ctx.isCollaboratorOnProject ||
            resolveCollaboratorCapability({ ...ctx, capabilityKey: "canViewPlanning" }) ||
            base.canViewPlanning));
      return;
    }
    resolved[key] = base[key] === true;
  });

  return {
    ...applyPermissionImplications(resolved, { role, accessScope }, explicitValues),
    ...resolveClientPortalModulePermissions({
      ...ctx,
      assignmentFirstClientModules: context.assignmentFirstClientModules,
      clientAssignmentLoading: context.clientAssignmentLoading,
    }),
    ...resolveClientSupplierInteractionPermissions({
      ...ctx,
      assignmentFirstClientModules: context.assignmentFirstClientModules,
      clientAssignmentLoading: context.clientAssignmentLoading,
    }),
    ...resolveClientProductSelectionDecisionPermissions({
      ...ctx,
      assignmentFirstClientModules: context.assignmentFirstClientModules,
      clientAssignmentLoading: context.clientAssignmentLoading,
    }),
    ...resolveScheduleAssignmentPermissionFlags(ctx),
    ...resolveDesignBoardAssignmentPermissionFlags({
      ...ctx,
      assignmentFirstClientModules: context.assignmentFirstClientModules,
      clientAssignmentLoading: context.clientAssignmentLoading,
    }),
  };
}

function resolveScheduleAssignmentPermissionFlags(ctx = {}) {
  const role = safeStr(ctx.matchedCollaborator?.role || "collaborator").toLowerCase();
  const perms =
    ctx.assignmentUidDoc?.permissions ||
    ctx.matchedCollaborator?.permissions ||
    {};
  const normalized = normalizeSchedulePermissionsForAssignmentSave(perms, {
    rawPermissions: perms,
    role,
  });
  const canEditSchedule = ctx.isAdmin ? true : normalized.canEditSchedule === true;
  const canViewSchedule =
    ctx.isAdmin || normalized.canViewSchedule === true || canEditSchedule === true;
  return {
    canViewSchedule,
    canEditSchedule: ctx.isAdmin ? true : canEditSchedule,
  };
}

/**
 * Compare UI-facing collaborator row permissions vs rules-resolved capabilities.
 */
export function detectPlanningCapabilityMismatch(context = {}) {
  const ctx = buildPlanningCapabilityContext(context);
  const rulesResolved = resolvePlanningCapabilities(ctx);
  const rowPerms = normalizeCollaboratorPermissions(ctx.matchedCollaborator?.permissions || {}, {
    role: ctx.matchedCollaborator?.role,
    accessScope: ctx.matchedCollaborator?.accessScope,
  });

  const mismatchedKeys = COLLABORATOR_PERMISSION_KEYS.filter(
    (key) => Boolean(rowPerms[key]) !== Boolean(rulesResolved[key])
  );

  return {
    rulesResolved,
    rowPermissions: rowPerms,
    mismatchedKeys,
    hasMismatch: mismatchedKeys.length > 0,
  };
}
